import slug from 'slug';
import { i18n, type Locale } from '~/i18n/i18n-config';
import type { SiteLocale } from '~/libs/dato/graphql';
import type { DestinationQueryData } from '~/libs/dato/queries/destination';

type UrlProps = {
  _modelApiKey: string;
  slug?: string | null;
  shortSlug?: string | null;
};

// An external_page with a shortSlug is an internal path: NOT to be treated as an external link
const isExternalPage = (url: UrlProps | null | undefined): boolean =>
  url?._modelApiKey === 'external_page' && !url.shortSlug;

const extractTextFromNode = (node: any): string => {
  if (!node) return '';

  if (node.value) return node.value;

  if (node.children) {
    return node.children.map((child) => extractTextFromNode(child)).join(' ');
  }

  return '';
};

// Callers reach this through `as UrlProps` casts on nullable CMS link fields, so the null has to
// be handled here — the cast hides it from the compiler at every call site.
const getUrl = (url: UrlProps | null | undefined): string | null => {
  const key = url?._modelApiKey;
  const slug = url?.slug;
  if (!slug) return null;

  switch (key) {
    case 'external_page':
      // shortSlug is a path on this site: the locale-aware Link adds the locale prefix
      if (url?.shortSlug) return `/${url.shortSlug.replace(/^\/+/, '')}`;
      // otherwise the slug IS the full external URL, not a path segment
      return slug;
    // No 'destination' case — those need country/region/place segments: callers branch to getDestinationUrl
    case 'inspiration':
      return `/inspiration/${slug}`;
    default:
      return `/${slug}`;
  }
};

const getDestinationSlug = (text: string | null | undefined) => {
  if (!text) return '';

  // manually resolve complex destination urls where data not consistent in cms
  const destinationOverrides = {
    ' ': '-',
    'united kingdom': 'uk',
    'highlands & islands': 'scotland'
  };

  // dynamically create regex set of matching patterns from destinationOverrides keys
  const destinationRegEx = new RegExp(Object.keys(destinationOverrides).join('|'), 'g');

  // match exact word 'and' or '&' to avoid replacing within words eg england -> engl
  const joinerWordRegEx = /\b(and|&)\b/g;

  // use slug package to remove accented chars, must be last step
  const resolvedSlug = text
    .toLowerCase()
    .replace(destinationRegEx, (matched) => destinationOverrides[matched])
    .replace(joinerWordRegEx, '');
  return slug(resolvedSlug);
};

// TODO — delete if CMS quirks fixed:
// United Kingdom is a Region (not only a Country) and Scotland's Region ("Highlands & Islands") is named to match its page.
// getDestinationSlug's name overrides cover the same two quirks for URLs.
const UK_COUNTRY_PAGE_SLUG = 'uk-holidays-by-train';
const isUkCountryPage = (destinationSlug: string) => destinationSlug === UK_COUNTRY_PAGE_SLUG;
const isScotlandRegion = (region: { countries: ReadonlyArray<{ countryId: string }> }) =>
  region.countries.some((country) => country.countryId === 'GB-SCT');

// Only en-GB-pinned url* aliases are accepted: a localised display name selected alongside them can't reach URL construction.
type DestinationUrlPlace = {
  __typename: 'PlaceRecord';
  urlPlaceName: string | null;
  country: { urlCountryName: string | null };
};

type DestinationUrlRegion = {
  __typename: 'RegionRecord';
  urlRegionName: string | null;
  countries: ReadonlyArray<{ urlCountryName: string | null }>;
};

type DestinationUrlItem = {
  slug: string;
  destination?: DestinationUrlPlace | DestinationUrlRegion | null;
};

// City landing pages are Place records, a Place has a single country
const getPlaceSegments = ({ urlPlaceName, country }: DestinationUrlPlace) => [
  getDestinationSlug(country.urlCountryName),
  getDestinationSlug(urlPlaceName)
];

// Regional landing pages are Region records, a Region has multiple countries,
// where the region might be the same as the country
const getRegionSegments = ({ urlRegionName, countries }: DestinationUrlRegion) => {
  if (!urlRegionName) return null;

  const regionSlug = getDestinationSlug(urlRegionName);
  const [firstCountry] = countries;
  if (!firstCountry) return [regionSlug];

  const countrySlug = getDestinationSlug(firstCountry.urlCountryName);
  const isRegionCountry =
    countries.length === 1
      ? countrySlug === regionSlug
      : countries.some((country) => country.urlCountryName === urlRegionName);

  return isRegionCountry ? [regionSlug] : [countrySlug, regionSlug];
};

const getDestinationParentSegments = (destination: DestinationUrlItem['destination']) => {
  switch (destination?.__typename) {
    case 'PlaceRecord':
      return getPlaceSegments(destination);
    case 'RegionRecord':
      return getRegionSegments(destination);
    default:
      return null;
  }
};

const getDestinationUrl = ({ slug, destination }: DestinationUrlItem) => {
  // fallback if no destination (or no region name) set
  const parentSegments = getDestinationParentSegments(destination) ?? [slug.split('-')[0]];
  return ['/destination', ...parentSegments.filter(Boolean), slug].join('/');
};

type CountryCrumbMessages = (key: 'destination.uk' | 'destination.scotland') => string;

/**
 * City page parent-country crumb shared by the visible breadcrumb and its JSON-LD
 * Returns null when the page isn't a city page (no breadcrumb is shown).
 */
const getCountryCrumb = (record: DestinationQueryData['destination'], t: CountryCrumbMessages) => {
  const place = record?.destination;
  if (place?.__typename !== 'PlaceRecord' || !place.city) return null;

  const countryPage = record?.countryLandingPage;
  const countryRegion = countryPage?.destination?.__typename === 'RegionRecord' ? countryPage.destination : null;
  const href = countryPage ? getDestinationUrl({ slug: countryPage.slug, destination: countryRegion }) : null;

  if (countryRegion && isScotlandRegion(countryRegion)) return { label: t('destination.scotland'), href };
  if (countryPage && isUkCountryPage(countryPage.slug)) return { label: t('destination.uk'), href };
  return { label: countryRegion?.regionName ?? '', href };
};

const hasFeatureText = (featureText, regularText) => (isNotEmptyString(featureText) ? featureText : regularText);

// see https://github.com/datocms/structured-text/blob/main/packages/utils/src/guards.ts isEmptyDocument function
const isEmptyStructuredText = (item) =>
  item.value.schema === 'dast' &&
  item.value.document.children.length === 1 &&
  item.value.document.children[0].type === 'paragraph' &&
  item.value.document.children[0].children.length === 1 &&
  item.value.document.children[0].children[0].type === 'span' &&
  item.value.document.children[0].children[0].value === '';

const isNotEmptyString = (text: any): text is string => typeof text === 'string' && text.trim() !== '';

const calculateReadingTime = (text: string) => {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return {
    minutes,
    text: `${minutes} min read`
  };
};

const ExtractTextFromStructuredTextElement = (body) => {
  if (!body?.value?.document?.children) return '';

  return body.value.document.children.map((node) => extractTextFromNode(node)).join(' ');
};

/*
 * Helpers to convert Nextjs kebab-case locales (e.g. 'en-GB') <> DatoCMS snake_case locales (e.g. 'en_GB')
 * see https://community.datocms.com/t/why-is-datocms-using-zh-cn-locale-in-the-ui-and-in-the-backend-zh-cn/3739
 */

const kebabToSnake = (input: string[] | string | undefined) => {
  if (Array.isArray(input)) {
    return input.map((str) => str.replaceAll('-', '_'));
  } else if (typeof input === 'string') {
    return input.replaceAll('-', '_');
  } else {
    return input;
  }
};

const snakeToKebab = (input: string): string => input.replaceAll('_', '-');

const getMiddlewareLocales = (datoLocales: readonly SiteLocale[]): Locale[] =>
  datoLocales.map(snakeToKebab).filter((locale): locale is Locale => i18n.locales.some((l) => l === locale));

const getDatoLocale = (lang: string): SiteLocale => {
  const locale = i18n.locales.some((l) => l === lang) ? lang : i18n.defaultLocale;
  return kebabToSnake(locale) as SiteLocale;
};

const defaultDatoLocale = getDatoLocale(i18n.defaultLocale);

/* End of Nextjs <> DatoCMS locale helpers */

export {
  calculateReadingTime,
  defaultDatoLocale,
  ExtractTextFromStructuredTextElement,
  extractTextFromNode,
  getDatoLocale,
  hasFeatureText,
  isEmptyStructuredText,
  isNotEmptyString,
  kebabToSnake,
  snakeToKebab,
  getMiddlewareLocales,
  getCountryCrumb,
  getDestinationSlug,
  getDestinationUrl,
  isUkCountryPage,
  getUrl,
  isExternalPage,
  type UrlProps
};
